import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ErrorState, TableSkeleton } from "@/components/common/DataStates";
import { agentMeta } from "@/lib/agents";
import { get } from "@/lib/api";
import { formatCurrency, formatNumber } from "@/lib/format";

const limitLabels = [
  ["brands", "Marques"],
  ["users", "Utilisateurs"],
  ["content_items_month", "Contenus / mois"],
  ["ai_budget_usd_month", "Budget IA / mois"],
  ["leads", "Leads"],
  ["storage_mb", "Stockage"],
];

const moduleLabels = {
  social: "Réseaux sociaux",
  crm: "CRM",
  approvals: "Validations",
  brand_brain: "Mémoire de marque",
  meetings: "Réunions",
  proposals: "Propositions",
  analytics: "Analyses",
  automations: "Automatisations",
  commerce: "Commerce",
  admin: "Administration",
};

const present = (value) => value === null || value === undefined || value === "" ? "—" : String(value);
const validNumber = (value) => typeof value === "number" && Number.isFinite(value);

function formatLimit(key, value) {
  if (!validNumber(value)) return "—";
  if (key === "ai_budget_usd_month") return `${formatNumber(value)} USD`;
  if (key === "storage_mb") {
    return value >= 1024 && value % 1024 === 0
      ? `${formatNumber(value / 1024)} Go`
      : `${formatNumber(value)} Mo`;
  }
  return formatNumber(value);
}

function FeatureList({ values, label }) {
  const names = Array.isArray(values)
    ? values.filter((value) => typeof value === "string" && value !== "").map((value) => (
      label === "Agents inclus" ? agentMeta(value).persona : moduleLabels[value] || "—"
    ))
    : [];
  return <div>
    <h4 className="text-sm font-medium">{label}</h4>
    {names.length ? <ul className="mt-2 flex min-w-0 flex-wrap gap-2">
      {names.map((name, index) => <li key={`${name}-${index}`} className="max-w-full break-all rounded-md border border-border bg-muted/40 px-2.5 py-1 text-xs">{name}</li>)}
    </ul> : <p className="mt-2 text-sm text-muted-foreground">—</p>}
  </div>;
}

function PlanCard({ plan, index }) {
  const limits = plan.limits && typeof plan.limits === "object" && !Array.isArray(plan.limits) ? plan.limits : {};
  const features = plan.features && typeof plan.features === "object" && !Array.isArray(plan.features) ? plan.features : {};
  const price = validNumber(plan.price_monthly) && typeof plan.currency === "string" && /^[A-Z]{3}$/.test(plan.currency)
    ? `${formatCurrency(plan.price_monthly, plan.currency)} / mois`
    : "—";
  const status = typeof plan.is_active === "boolean" ? (plan.is_active ? "Actif" : "Inactif") : "—";

  return <article className="og-card min-w-0 overflow-hidden p-5 sm:p-6" aria-labelledby={`plan-title-${index}`} data-testid="platform-plan-card">
    <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <h3 id={`plan-title-${index}`} className="break-words font-heading text-xl font-semibold">{present(plan.name)}</h3>
        <p className="mt-1 break-all text-xs text-muted-foreground">Code : {present(plan.code)}</p>
      </div>
      <span className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-medium ${plan.is_active === true ? "bg-emerald-50 text-emerald-800" : "bg-muted text-muted-foreground"}`}>{status}</span>
    </div>
    <p className="mt-5 break-words font-heading text-2xl font-semibold">{price}</p>
    <p className="mt-3 break-words text-sm text-muted-foreground">{present(plan.description)}</p>
    <section className="mt-6 border-t border-border pt-5" aria-label="Limites du plan">
      <h4 className="font-heading text-base font-semibold">Limites</h4>
      <dl className="mt-3 grid min-w-0 grid-cols-2 gap-x-4 gap-y-3">
        {limitLabels.map(([key, label]) => <div key={key} className="min-w-0">
          <dt className="text-xs text-muted-foreground">{label}</dt>
          <dd className="mt-1 break-words text-sm font-medium">{formatLimit(key, limits[key])}</dd>
        </div>)}
      </dl>
    </section>
    <section className="mt-6 space-y-4 border-t border-border pt-5" aria-label="Fonctionnalités du plan">
      <h4 className="font-heading text-base font-semibold">Fonctionnalités</h4>
      <FeatureList values={features.agents} label="Agents inclus" />
      <FeatureList values={features.modules} label="Modules inclus" />
    </section>
  </article>;
}

export default function PlatformPlansPage() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["platform-admin", "plans"],
    queryFn: () => get("/admin/plans"),
    retry: false,
  });
  const plans = Array.isArray(data) ? data.filter((plan) => plan && typeof plan === "object") : [];
  const sorted = [...plans].sort((a, b) => {
    const orderA = validNumber(a.sort_order) ? a.sort_order : Infinity;
    const orderB = validNumber(b.sort_order) ? b.sort_order : Infinity;
    return orderA - orderB;
  });

  return <div className="min-w-0 space-y-6" data-testid="platform-plans">
    <header>
      <p className="og-section-label">Administration · lecture seule</p>
      <h2 className="mt-1 font-heading text-2xl font-semibold">Plans</h2>
      <p className="mt-1 text-sm text-muted-foreground">Configuration commerciale des offres OneGate</p>
    </header>
    {isLoading ? <div className="og-card"><TableSkeleton testId="plans-loading" /></div>
      : isError ? <ErrorState message="Impossible de charger les plans." onRetry={refetch} />
        : sorted.length ? <div className="grid min-w-0 items-start gap-5 lg:grid-cols-2">
          {sorted.map((plan, index) => <PlanCard key={plan.id || index} plan={plan} index={index} />)}
        </div>
          : <div className="og-card p-6 text-sm text-muted-foreground">Aucun plan disponible.</div>}
  </div>;
}